import { Transition, TransitionChild } from "@headlessui/react";
import { Carousel, useCarousel } from "nuka-carousel";
import { useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { ModalProps } from "src/components/modals/Modal";
import { isServer } from "src/utils/isServer";
import { modals, useModal } from "src/utils/modals";
import tw from "src/utils/tw";

interface Props {
  images: string[];
  index: number;
}

function NavButtons() {
  const { currentPage, goBack, goForward, totalPages } = useCarousel();

  return (
    <div className="flex items-center justify-between">
      <button
        className={tw(
          "h-12 w-12 rounded-full bg-dark sm:hidden",
          currentPage === 0 && "cursor-default opacity-50"
        )}
        onClick={goBack}
        type="button"
      >
        <i className="far fa-arrow-left text-white" />
      </button>

      <div className="relative px-4 text-center text-sm text-white">
        Image {currentPage + 1} of {totalPages}
      </div>

      <button
        className={tw(
          "h-12 w-12 rounded-full bg-dark sm:hidden",
          currentPage === totalPages - 1 && "cursor-default opacity-50"
        )}
        onClick={goForward}
        type="button"
      >
        <i className="far fa-arrow-right text-white" />
      </button>
    </div>
  );
}

export const ImageModal = modals.create((props: Props) => {
  const modal = useModal();

  // console.log("props", props);

  function onCancel() {
    modal.hide();
  }

  return (
    <ImageModalContainer
      visible={modal.visible}
      afterClose={modal.remove}
      handleCancel={onCancel}
    >
      <Carousel showArrows arrows={<NavButtons />}>
        {props.images.map((i) => {
          return (
            <div
              key={i}
              className="flex min-w-full items-center justify-center px-4"
            >
              <div className="mb-6 text-center">
                <img
                  key={i}
                  src={i}
                  alt="Image"
                  className="max-h-[600px] rounded-md"
                />
              </div>
            </div>
          );
        })}
      </Carousel>
    </ImageModalContainer>
  );
});

function ImageModalContainer(props: ModalProps) {
  const container = useMemo(() => {
    return isServer
      ? null
      : document.body.appendChild(document.createElement("div"));
  }, []);

  useEffect(() => {
    return () => {
      container?.remove();
    };
  }, []);

  return ReactDOM.createPortal(<ImageModalBody {...props} />, container);
}

function ImageModalBody(props: ModalProps) {
  return (
    <Transition
      show={props.visible}
      className={tw("fixed inset-0 z-40", props.className)}
      afterLeave={props.afterClose}
      as="div"
    >
      <div className={tw("min-h-screen", "xpx-4", "text-center")}>
        <TransitionChild
          className="fixed inset-0 bg-dark bg-opacity-90 duration-200 data-[closed]:opacity-0"
          onClick={props.handleCancel}
          as="div"
        >
          <button
            className="fixed right-4 top-4 h-12 w-12 rounded-full bg-dark"
            onClick={props.handleCancel}
            type="button"
          >
            <i className="far fa-xmark text-white" />
          </button>
        </TransitionChild>

        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          className={tw(
            "my-4 max-w-5xl align-middle data-[closed]:scale-95 data-[closed]:opacity-0",
            "inline-block w-full transform text-left transition-all duration-200"
          )}
          as="div"
        >
          <div className={tw("xbg-error", "overflow-hidden")}>
            {props.children}
          </div>
        </TransitionChild>
      </div>
    </Transition>
  );
}
